/* V2 */
import { IAdvicePrescription, IConsumingSince, IDiagnosis, IDiagnosticLabTestItem, IDoctorCheckInInfo, IFormulation, ILabtestPrescription, IMedicineDuration, IMedicineFrequency, IPatientNote, ISymptom, IUnreadMessageInfo } from "@curefit/care-common";
import { IMedicationInfo, IMedicinePrescription, IMentalStatusExamination, IPastPsychiatricHistory, IPatientAllergy, IPatientConcern, IPatientExamination, IPatientHabits, IPatientRedFlag, IPatientSurgeryAndProcedure, IPatientSystemiscExamination, IPatientSystemReview } from "@curefit/ehr-common";
import { ConsultationPrescriptionItemRequest, DiagnosticPrescriptionItemRequest, DietPrescriptionItemRequest, GMFEntitySource, HabitPrescriptionItemRequest, MedicinePrescriptionItemRequest, UserGoalView, UserTrackableSymptomUpsertRequest } from "@curefit/gmf-common";
import { IAddMetric, IMetricOptions } from "@curefit/metrics-common";
import * as _ from "lodash";
import { AppConstants } from "../utils/AppConstants";
import AppUtils from "../utils/AppUtils";
import { TabConfig } from "../utils/TabManager";
import ILifestyleGoal, { ILifestyleGoalIdealValue, ILifestyleGoalIdealValueRange } from "./ILifestyleGoal";
import IChiefComplaint from "./IChiefComplaint";
import IConditionsUI from "./IConditionsUI";
import IDietPlan, { IDietPlanPrescriptionDetail } from "./IDietPlan";
import IDietRecall from "./IDietRecall";
import IDietRecallMealItem, { IDietRecallFoodOptionValue } from "./IDietRecallMealItem";
import IDifferentialDiagnosis from "./IDifferentialDiagnosis";
import IFetchMealsByCaloriesRequest from "./IFetchMealsByCaloriesRequest";
import { IFitnessSchedule, PrescriptionItemFitnessGuideline } from "./IFitnessPrescription";
import IFoodGroup from "./IFoodGroup";
import IFrequency from "../../../common/models/IFrequency";
import IHabitNew from "./IHabitNew";
import { IHabitItem } from "./IHabitsPrescription";
import ILabTest from "./ILabTest";
import ILocalStorageObject from "./ILocalStorageObject";
import IMealItem from "./IMealItem";
import IMealOption from "../../../common/models/IMealOption";
import IMealSlot from "./IMealSlot";
import IMeditationPrescription, { IMeditationActivities } from "./IMeditationPrescription";
import IPatientCondition from "./IPatientCondition";
import IPatientVital from "./IPatientVital";
import { IPatientVitalsUI } from "./IPatientVitalResponse";
import IPPHUI from "./IPPHUI";
import IRecurringSymptom from "./IRecurringSymptom";
import { IRecurringSymptomFrequency } from "./IRecurringSymptomFrequency";
import IRoomEventLog from "./IRoomEventLog";
import { IFollowup, IHabitListItem, IPlanActivity, IReferral } from "./LCPlanClientModels";
import IPatientLifestylePlanWrapper from "./patients/IPatientLifestylePlanWrapper";
import IPatientWorkoutPreference from "./patients/IPatientWorkoutPreference";
import IStaticDataValue from "../../../common/models/IStaticDataValue";
import { IPlanRequestMetaWrapper } from "../../../common/models/IPlanRequestMeta";
import {ITreatmentPlanTemplateSaveObj} from "./ITreatmentPlanTemplateResponse";

export default class ModelFactory {

    static createNewPatientVital (value: string, vitalName: string): IPatientVital {
        let patientVital: IPatientVital = {
            vitalValue: value,
            internalValue: vitalName.toLowerCase(),
            displayValue: vitalName
        }
        return patientVital
    }

    static creatNewSurgeryAndProcedure(): Partial<IPatientSurgeryAndProcedure> {
        return {
            internalValue: null,
            displayValue: null,
            code: null,
            comment: null,
            year: null,
            patientId: null
        }
    }

    static createNewPatientHabit(): Partial<IPatientHabits> {
        return {
            id: null,
            subHabits: null,
            habitCode: null
        }
    }

    static createNewHabit(): IHabitNew {
        return {
            id: null,
            habitName: null,
            frequency: null
        }
    }

    static createNewPatientCondition(): IPatientCondition {
        return {
            internalValue: null,
            displayValue: null,
            onSetAge: null,
            startMonth: null,
            startYear: null,
            endMonth: null,
            endYear: null,
            comment: null,
            tillDate: null,
            relationship: null,
            source: null,
            lastEditedPersonId: null,
            personType: null
        }
    }

    static createNewFormattedConditionsItem(): IConditionsUI {
        return {
            name: null,
            items: null
        }
    }

    static createNewStaticItem(): IStaticDataValue {
        return {
            displayValue: null,
            internalValue: null
        }
    }

    static isValidSurgeryAndProcedure(surgery: IPatientSurgeryAndProcedure): boolean {
        return !_.isEmpty(surgery.displayValue)
    }

    static isValidPatientCondition(condition: IPatientCondition): boolean {
        return !_.isEmpty(condition.internalValue)
    }

    static createNewAllergy(): Partial<IPatientAllergy> {
        return {
            comment: null,
            internalValue: null,
            displayValue: null,
            allergenType: null,
            source: null,
            appointmentId: null,
            isDeleted: false
        }
    }

    static createNewMedication(): Partial<IMedicinePrescription> {
        return {
            code: null,
            comment: null,
            medicineType: null,
            brand: null,
            name: null,
            intakeCondition: null,
            slotList: [],
            medicineFrequency: null,
            medicineDuration: null,
            dosage: null,
            patientId: null
        }
    }

    static createNewMedicineDuration(): IMedicineDuration {
        return {
            countDays: null,
            count: null,
            unit: null
        }
    }

    static createNewMedicineFrequency(): IMedicineFrequency {
        return {
            unit: null,
            weeklySchedule: null,
            count: null
        }
    }

    static createNewConsumingSinceObject(): IConsumingSince {
        return {
            month: null,
            year: null
        }
    }

    static createNewSymptom(): ISymptom {
        return {
            internalValue: null,
            displayValue: null,
            type: null,
            comment: null,
            duration: null
        }
    }

    static createNewTrackableSymptomSource(): GMFEntitySource {
        return {
            serviceId: null,
            serviceRefId: null,
            userType: null,
            userRefId: null,
            sourceFamily: null
        }
    }

    static createNewTrackableSymptom(): UserTrackableSymptomUpsertRequest {
        return {
            userTrackableSymptomId: null,
            userTrackableSymptomObj: {
                userId: null,
                secondaryUserId: null,
                isSelf:  null,
                source: ModelFactory.createNewTrackableSymptomSource(),
                symptomInternalValue: null,
                symptomDisplayValue: null,
                freq: {
                    count: null,
                    unit: null
                },
                status: null
            }
        }
    }

    static createNewConcern(): Partial<IPatientConcern> {
        return {
            internalValue: null,
            displayValue: null,
            lastOccurence: null,
            frequencyUnit: null,
            frequencyCount: null,
            comment: null,
            lastEditedPersonId: null,
            personType: null,
            source: null
        }
    }

    static createNewFrequency(unit?: string, count?: string): IFrequency {
        return {
            unit: unit || null,
            count: count || null
        }
    }

    static createNewDiagnosticLabTest(): Partial<ILabtestPrescription> {
        return {
            name: null,
            testCondition: null,
        }
    }

    static createNewLabTest(): ILabtestPrescription {
        return {
            name: null,
            testCondition: null,
            code: null,
            testDate: null
        }
    }

    static createNewDoctorCheckinInfo(): IDoctorCheckInInfo {
        return {
            doctorType: null,
            doctorSpecialityCode: null,
            checkinType: null,
            consultationType: null,
            comment: null,
            remind: null,
            doctor: null,
            duration: null,
            activityDate: null,
            frequency: null
        }
    }

    static createReferralItem(): IReferral {
        return {
            when: null,
            comment: "",
            doctorSpecialityCode: null
        }
    }

    static createNewRecurringSymptom(): IRecurringSymptom {
        return {
            id: null,
            trouble: null,
            frequency: this.createNewRecurringSymptomFrequency(),
            lastOccurence: null,
            comments: null
        }
    }

    static createNewRecurringSymptomFrequency(): IRecurringSymptomFrequency {
        return {
            frequencyCount: null,
            frequencyUnit: null
        }
    }

    static createNewRedFlag(): Partial<IPatientRedFlag> {
        return {
            troubles: null,
            comment: null,
            source: null,
            lastEditedPersonId: null,
            personType: null
        }
    }

    static createNewExam(type?: string): Partial<IPatientExamination> {
        return {
            type: type !== undefined ? type : null,
            examination: null,
            lastEditedPersonId: null,
            personType: null,
            source: null,
            comment: null
        }
    }

    static createNewSystemicExam(examCode?: string): Partial<IPatientSystemiscExamination> {
        return {
            systemicExaminationCode: !_.isEmpty(examCode) ? examCode : null,
            systemicExaminationTagsSet: null,
            source: null,
            isNormal: null,
            comment: null,
            personType: null,
            lastEditedPersonId: null
        }
    }

    static createNewDiagnosis(): IDiagnosis {
        return {
            diagnosis: null,
            diagnosisType: AppConstants.DiagnosisType.FINAL,
            comment: null,
            code: null,
            codeText: null
        }
    }

    static createNewDifferentialDiagnosis(): IDifferentialDiagnosis {
        return {
            differentialDiagnosis: null,
            notes: null
        }
    }

    static createNewChiefComplaint(): Partial<IChiefComplaint> {
        return {
            illness: null,
            complaints: null
        }
    }

    static createNewUnreadMessageInfo(): IUnreadMessageInfo {
        return {
            unreadMessageCount: null,
            userId: null,
            userType: null
        }
    }

    static createNewLabTestObject(): ILabTest {
        return {
            testId: null,
            testCount: null,
            normalCount: null,
            abnormalCount: null,
            reportDetails: null,
            childCheckUpReportInfo: null
        }
    }

    static createNewCarefitLocalStorageObject(): ILocalStorageObject {
        return {
            prescription: null,
            mealsMap: null,
            unsavedPrescriptions: null
        }
    }

    static createMentalStatusExaminationObject(category: string, subcategory: string): Partial<IMentalStatusExamination> {
        return {
            category: category || null,
            subCategory: subcategory || null,
            observations: null,
            appointmentId : null,
            isDeleted: false,
            lastEditedPersonId: null,
            personType: "doctor",
            source: "APP",
            patientId: null
        }
    }

    static createNewReviewOfSystemsObject(categoryCode?: string): IPatientSystemReview {
        return {
            category: !_.isEmpty(categoryCode) ? categoryCode : null,
            noProblem: null,
            otherSymptoms: null,
            symptoms: null,
            appointmentId : null,
            isDeleted: false,
            lastEditedPersonId: null,
            personType: "doctor",
            source: "APP",
            patientId: null
        }
    }

    static createFormulationObject(factor: string): IFormulation {
        return {
            id: null,
            comment: null,
            factor: factor,
            lastEditedPersonId: null,
            personType: null,
            source: null
        }
    }

    static createNewPatientNoteObject(): IPatientNote {
        return {
            id: null,
            note: null,
            lastEditedPersonId: null,
            personType: null,
            source: null
        }
    }

    static createNewDietRecallMealItem(): IDietRecallMealItem {
        return {
            mealType: null,
            day: null,
            time: null,
            mealOptions: null,
            insight: null
        }
    }

    static createNewDietPlanItem(): IDietPlan {
        return {
            guidelines: null,
            start: null,
            end: null,
            prescriptionDetail: ModelFactory.createNewDietPrescriptionDetail()
        }
    }

    static createNewDietPrescriptionDetail(): IDietPlanPrescriptionDetail {
        return {
            overallDietThemes: null,
            meals: null
        }
    }

    static createNewMealSlot(): IMealSlot {
        return {
            mealSlot: null,
            recommendedTimeOfDay: null,
            details: this.createNewMealDetail()
        }
    }

    static createNewDietPlanPrescriptionItem(): DietPrescriptionItemRequest {
        return {
            category: "FOOD",
            secondaryUserId: null,
            startEpoch: null,
            endEpoch: null,
            status: null,
            prevPresId: null,
            onlyGuidelines: null,
            guidelines: null,
            overallDietThemes: null,
            meals: null,
            themeIds: null,
            editInfo: null,
            isSelf: null
        }
    }

    static createNewMealDetail(): IMealOption {
        return {
            mealIds: null,
            calories: null,
            calorieBreakup: null,
            compliance: null,
            themes: null
        }
    }

    static createNewCalorieBreakup(): IFoodGroup {
        return {
            foodgroups: null,
            contribution: null
        }
    }

    static createNewFetchMealsByCaloriesRequest(): IFetchMealsByCaloriesRequest {
        return {
            mealSlot: null,
            foodGroupBreakup: null,
            calorieRange: null
        }
    }

    static createNewTabConfig(): TabConfig {
        return {
            id: null,
            tabName: null,
            display: null,
            component: null
        }
    }

    static createNewFitnessPlanPrescriptionItem(): Partial<IPlanActivity> {
        return {
            "reminder": false,
            "activity": null,
            "frequency": null,
            "duration": null,
            "advice": null
        }
    }

    static createNewFitnessSchedule(): IFitnessSchedule {
        return {
            frequency: null,
            repetition: ModelFactory.createNewFrequency(),
            slots: null,
            slotBreakup: []
        }
    }

    static createNewDietRecall(): IDietRecall {
        return {
            foodDislikesv1: null,
            preferredCuisinesv1: null,
            preferredFoodTypesv1: null,
            meals: null,
            beveragesv1: null,
            breadsv1: null,
            fruitsv1: null,
            lentilsv1: null,
            meatsv1: null,
            nonPreferredDishesv1: null,
            preferredDishesv1: null,
            vegetablesv1: null
        }
    }

    static createNewHabitItem(): Partial<IHabitItem> & { item: string} {
        return {
            frequency: null,
            comment: null,
            item: null
        }
    }

    static createNewWorkoutPreferenceItem(): Partial<IPatientWorkoutPreference> {
        return {
            categories: null,
            preferredDays: null,
            preferredSlots: null,
            regular: null,
            weeklySchedule: null,
            workouts: null,
            frequency: null,
            duration: null,
            timesOfDay: null,
            type: null
        }
    }

    static createNewPatientPlan(): Partial<IPatientLifestylePlanWrapper> {
        return {
            dietPrescription: null,
            fitnessPrescription: null,
            mindfulnessPrescription: null,
            habitsPrescription: null,
            consultations: null,
            consultationsByTherapist: null,
            consultationsByLifestyleCoach: null,
            diagnosticAssessment: null,
            diagnosticAssessmentByTherapist: null,
            medicinePlanPrescription: null,
            medicinePlanPrescriptionByTherapist: null
        }
    }

    static createNewLifestyleGoalWrapper(startDate: number, endDate?: number): Partial<UserGoalView> {
        return {
            userId: null,
            goalId: null,
            startEpoch: startDate !== undefined ? startDate : null,
            endEpoch: endDate !== undefined ? endDate : null,
            userGoalType: null,
            editInfo: null,
            status: null,
            durationInDays: null,
            goalMetricTargets: null
        }
    }

    static createNewLifestyleGoal(): ILifestyleGoal {
        return {
            aim: null,
            metricId: null,
            priority: null,
            targets: null,
            idealValue: ModelFactory.createNewLifestyleGoalIdealValue(),
            currentValue: ModelFactory.createNewLifestyleGoalIdealValue(),
            trackingFreq: ModelFactory.createNewFrequency()
        }
    }

    static createNewLifestyleGoalIdealValue(): ILifestyleGoalIdealValue {
        return {
            value: null,
            range: ModelFactory.createNewLifestyleGoalIdealRange()
        }
    }

    static createNewLifestyleGoalIdealRange(): ILifestyleGoalIdealValueRange {
        return {
            min: null,
            max: null
        }
    }

    static createNewHabitPlanPrescriptionItem(): HabitPrescriptionItemRequest {
        return {
            category: "HABIT",
            startEpoch: null,
            endEpoch: null,
            status: null,
            prevPresId: null,
            onlyGuidelines: null,
            guidelines: null,
            activities: [],
            editInfo: null,
            secondaryUserId: null,
            isSelf: null
        }
    }

    static createNewConsultationPlanPrescriptionItem(): ConsultationPrescriptionItemRequest {
        return {
            category: "CONSULTATION",
            startEpoch: null,
            endEpoch: null,
            status: null,
            prevPresId: null,
            onlyGuidelines: null,
            guidelines: null,
            activities: [],
            editInfo: null,
            secondaryUserId: null,
            isSelf: null
        }
    }

    static createNewConsultationPlanActivity(): any {
        return {
            productId: null,
            checkinType: null,
            comments: null,
            compliance: null,
            consultationMode: null,
            doctorId: null,
            doctorType: null,
            doctorTypeDisplayValue: null,
            quantity: null,
            schedule: null
        }
    }

    static createNewDiagnosticAssessmentObject(): DiagnosticPrescriptionItemRequest {
        return {
            category: "DIAGNOSTICS",
            secondaryUserId: null,
            startEpoch: null,
            endEpoch: null,
            status: null,
            prevPresId: null,
            onlyGuidelines: false,
            guidelines: [],
            activities: [],
            editInfo: null,
            isSelf: null
        }
    }

    static createBuildPlanMedicineObject(): MedicinePrescriptionItemRequest {
        return {
            secondaryUserId: null,
            category: "MEDICINE",
            startEpoch: null,
            endEpoch: null,
            status: null,
            prevPresId: null,
            onlyGuidelines: false,
            guidelines: [],
            activities: [],
            editInfo: null,
            isSelf: null
        }
    }

    static isValidAllergy(allergy: IPatientAllergy): boolean {
        return !_.isEmpty(allergy.internalValue)
    }

    static isAllergyEmpty(allergy: IPatientAllergy): boolean {
        return _.isEmpty(allergy.displayValue) && _.isEmpty(allergy.allergenType) && _.isEmpty(allergy.comment)
    }

    static isValidHabit(habit: IHabitNew): boolean {
        return !_.isEmpty(habit.habitName)
    }

    static isValidSymptom(symptom: ISymptom): boolean {
        return !_.isEmpty(symptom.displayValue) && symptom.displayValue.length < 255
    }

    static isValidConcern(concern: IPatientConcern): boolean {
        return !_.isEmpty(concern.displayValue)
    }

    static isValidMedication(medication: IMedicinePrescription): boolean {
        return !_.isEmpty(medication.name) && this.isValidDurationInMedication(medication.medicineDuration)
    }

    static isValidLifeStyleAdvice(lifeStyle: IAdvicePrescription): boolean {
        return !_.isEmpty(lifeStyle.name) && lifeStyle.name.length < 512
    }

    static isValidLabTest(labTest: ILabtestPrescription | IDiagnosticLabTestItem): boolean {
        return !_.isEmpty(labTest.name) && !_.isNil(_.get(labTest, "value"))
    }

    static isValidLabTestInPrescription(labTest: ILabtestPrescription): boolean {
        return !_.isEmpty(labTest.name);
    }

    static isValidDiagnosticLabTest(labTest: IDiagnosticLabTestItem): boolean {
        return AppUtils.isDefined(labTest.name) && AppUtils.isDefined(labTest.metricDate) && AppUtils.isDefined(labTest.metricId) && AppUtils.isDefined(labTest.value);
    }

    static isValidNote(note: IPatientNote): boolean {
        return !_.isEmpty(note.note)
    }

    static isValidReferral(referral: IReferral): boolean {
        return !_.isEmpty(referral.doctorSpecialityCode) && referral.doctorSpecialityCode !== "";
    }

    static isValidFollowUp(followUp: IFollowup): boolean {
        return !_.isEmpty(followUp) && !_.isEmpty(followUp.followUp);
    }

    static isValidMealSlot(mealSlot: IMealSlot): boolean {
        return !_.isEmpty(mealSlot.details)
    }

    static isValidCondition(condition: IPatientCondition): boolean {
        return !_.isEmpty(condition.displayValue);
    }

    static isConditionDetailsEmpty(condition: IPatientCondition): boolean {
        return !_.isEmpty(condition.startYear) && !_.isEmpty(condition.startMonth) &&
            !_.isEmpty(condition.onSetAge) && !_.isEmpty(condition.endYear) && !_.isEmpty(condition.endMonth) &&
            !_.isEmpty(condition.tillDate) && !_.isEmpty(condition.comment);
    }

    static isValidDiagnosis(diagnosisItem: IDiagnosis): boolean {
        return (!_.isEmpty(diagnosisItem.codeText) && (diagnosisItem.codeText.length < 128) && (!_.isEmpty(diagnosisItem.diagnosis) && diagnosisItem.diagnosis.length < 512))
    }

    static isValidDifferentialDiagnosis(differentialDiagnosis: IDifferentialDiagnosis): boolean {
        return !_.isEmpty(differentialDiagnosis.differentialDiagnosis) || !_.isEmpty(differentialDiagnosis.notes)
    }

    static isValidDurationInMedication(duration: IMedicineDuration): boolean {
        let status = false;
        if (!_.isEmpty(duration)) {
            if ((duration.count === null || duration.count === undefined) && _.isEmpty(duration.unit)) status = true
            else if ((duration.count !== null && duration.count !== undefined) && !_.isEmpty(duration.unit)) status = true
        } else {
            status = true;
        }
        return status;
    }

    static isValidFitnessPrescriptionItem(planActivityItem: IPlanActivity): boolean {
        return !_.isEmpty(planActivityItem.activity) && !_.isEmpty(planActivityItem.activity.internalValue) && AppUtils.isDefined(planActivityItem.duration) && !_.isEmpty(planActivityItem.frequency);
    }


    static isValidDiagnosticPlanPrescriptionItem(diagnosticPlanItem: DiagnosticPrescriptionItemRequest): boolean {
        return !_.isEmpty(diagnosticPlanItem.activities) && !_.isEmpty(diagnosticPlanItem.activities[0].name) && !_.isEmpty(diagnosticPlanItem.activities[0].schedule) &&
            AppUtils.isDefined(diagnosticPlanItem.activities[0].schedule.frequency) && diagnosticPlanItem.activities[0].schedule.frequency.toString() !== "" && diagnosticPlanItem.activities[0].schedule.frequency !== 0 &&
            !_.isEmpty(diagnosticPlanItem.activities[0].schedule.repetition) && !_.isEmpty(diagnosticPlanItem.activities[0].schedule.repetition.unit);
    }

    static isValidMedicinePlanPrescriptionItem(medicinePlanItem: MedicinePrescriptionItemRequest): boolean {
        return !_.isEmpty(medicinePlanItem.activities) && !_.isEmpty(medicinePlanItem.activities[0].brand) && !_.isEmpty(medicinePlanItem.activities[0].name) && !_.isEmpty(medicinePlanItem.activities[0].schedule) &&
            AppUtils.isDefined(medicinePlanItem.activities[0].schedule.frequency) && medicinePlanItem.activities[0].schedule.frequency.toString() !== "" && medicinePlanItem.activities[0].schedule.frequency !== 0 &&
            !_.isEmpty(medicinePlanItem.activities[0].schedule.repetition) && !_.isEmpty(medicinePlanItem.activities[0].schedule.repetition.unit);
    }

    static isReferralItemEmpty(referral: IReferral): boolean {
        return _.isEmpty(referral.doctorSpecialityCode)
    }

    static isFollowUpItemEmpty(followUp: IFollowup): boolean {
        return _.isEmpty(followUp.followUp)
    }

    static createNewPsychiatricHistoryDetailItem(): IPastPsychiatricHistory {
        return {
            id: null,
            appointmentMonth: null,
            appointmentYear: null,
            chiefComplaintsInternalValue: null,
            chiefComplaintsDisplayValue: null,
            doctorName: null,
            diagnosisInternalValue: null,
            diagnosisDisplayValue: null,
            medicationInfoList: [],
            appointmentId: null,
            isDeleted: false,
            lastEditedPersonId: null,
            personType: null,
            source: null,
            patientId: null
        }
    }

    static createNewPPHUIItem(): IPPHUI {
        return {
            appointmentMonth: null,
            appointmentYear: null,
            chiefComplaintsDisplayValue: null,
            diagnosisDisplayValue: null,
            items: []
        }
    }

    static createPPHMedicineItem(): IMedicationInfo {
        return {
            medicineName: null,
            frequency: null,
            duration: null,
            comments: null
        }
    }

    static isPPHEmpty(pphItem: IPastPsychiatricHistory) {
        let status = false;
        _.map(pphItem, (val, key) => {
            if (key !== "doctorName" && key !== "id" && val === null || val === undefined) {
                console.log("KEY", key);
                status = true;
            }
        });
        return status;
    }

    static isGoalItemEmpty(goal: UserGoalView, goalsType: string): boolean {
        if (goalsType === "PRIMARY") {
            return _.isEmpty(goal.goalId) && _.isEmpty(goal.userId)
        } else {
            return _.isEmpty(goal.goalName);
        }
    }

    static isTargetRangeNotValid (idealValue: ILifestyleGoalIdealValue, type: boolean): boolean | string {
        let status = true;
        let errorMessage = null;
        if (!_.isEmpty(idealValue) && !_.isEmpty(idealValue.range)) {
            if (AppUtils.isDefined(idealValue.range.max) && AppUtils.isDefined(idealValue.range.max) && Number(idealValue.range.min) > Number(idealValue.range.max)) {
                status = false;
                errorMessage = "Target Min can not be greater than target max."
            } else if ((AppUtils.isDefined(idealValue.range.max) && !AppUtils.isDefined(idealValue.range.min)) || (AppUtils.isDefined(idealValue.range.min) && !AppUtils.isDefined(idealValue.range.max))) {
                status = false;
                errorMessage = "Target Min and Target Max should be filled."
            }
        }
        if (type) return status
        else return errorMessage
    }

    static isValidGoal(goal: UserGoalView, goalType: string): boolean {
        let status = true;
        if (goalType === "PRIMARY") {
            if (!_.isEmpty(goal.goalId) && !_.isEmpty(goal.goalMetricTargets)) {
                _.map(goal.goalMetricTargets, (goalMetric) => {
                    if (!AppUtils.isDefined(goalMetric.metricId) ||
                        !AppUtils.isDefined(goalMetric.currentValue.value) || goalMetric.currentValue.value.toString() === "" ||
                        !ModelFactory.isValidGoalTarget(goalMetric.idealValue) || !ModelFactory.isTargetRangeNotValid(goalMetric.idealValue, true) || !AppUtils.isDefined(goalMetric.aim) ||
                        _.isEmpty(goalMetric.trackingFreq.unit) || !ModelFactory.isMilestonesListNotEmpty(goalMetric.targets)) {
                        status = false;
                        return false;
                    }
                })
            } else {
                status = false;
            }
        } else {
            if (_.isEmpty(goal.goalName)) status = false;
        }
        return status;
    }

    static isValidGoalTarget(idealValue: ILifestyleGoalIdealValue): boolean {
        let status = true;
        if ((!AppUtils.isDefined(idealValue.value) || idealValue.value.toString() === "") && (_.isEmpty(idealValue.range) || !AppUtils.isDefined(idealValue.range.max) || idealValue.range.max.toString() === "" ||
                !AppUtils.isDefined(idealValue.range.min) || idealValue.range.min.toString() === "")) {
            status = false;
        }
        return status;
    }

    static isMilestonesListNotEmpty(milestones: any[]): boolean {
        let status = true;
        if (!_.isEmpty(milestones)) {
            _.map(milestones, (target: any) => {
                if (AppUtils.isDefined(target.value) && target.value.toString() !== "") {
                    status = true;
                    return false;
                }
            })
        } else {
            status = false;
        }
        return status;
    }

    static getPlanActivityEmptyCol(activityItem: IPlanActivity): string {
        if (_.isEmpty(activityItem.activity) || (!_.isEmpty(activityItem.activity) && !AppUtils.isDefined(activityItem.activity.internalValue))) return "activity"
        else if (_.isEmpty(activityItem.frequency)) return "frequency"
        else if (!AppUtils.isDefined(activityItem.duration)) return "duration"
        else return null
    }

    static isValidMindfulnessPlanItem(mindfulnessPlanItem: IMeditationPrescription): boolean {
        let status = true;
        if (!_.isEmpty(mindfulnessPlanItem.activities)) {
            _.map(mindfulnessPlanItem.activities, (fitnessActivity: IMeditationActivities) => {
                if (_.isEmpty(fitnessActivity.meditationCategories) || _.isEmpty(fitnessActivity.meditationCategories[0]) ) {
                    status = false;
                    return false;
                }
            })
        } else {
            status = false;
        }
        return status;
    }

    static isValidHabitsPlanItem(habitsPlanItem: IHabitListItem): boolean {
        return !_.isEmpty(habitsPlanItem.dailyHabit) && !_.isEmpty(habitsPlanItem.dailyHabit.internalValue)
    }

    static createNewAddRoomEventLogObject(timestamp: number = null, localMemberIdentity: string = null, remoteMemberIdentity: string = null, debugMessage: string = null): IRoomEventLog {
        return {
            timestamp,
            localMemberIdentity,
            remoteMemberIdentity,
            debugMessage,
        }
    }

    static createNewDietRecallFoodOptionValue(): IDietRecallFoodOptionValue {
        return {
            count: null,
            name: null,
            unit: null,
            unitDetails: null,
            consumptionUnit: {
                unit: null,
                unitDetails: null
            }
        }
    }

    static createNewMealItem(): IMealItem {
        return {
            name: null,
            components: null,
            mealId: null,
            nutritionInfo: null,
            code: null,
            ingredientCategoryBreakup: null,
            mealSlots: null,
            mealTemplateId: null,
            popularityScore: null,
            shortDescription: null,
            source: null,
            status: null,
            tags: null,
            version: null,
            detailedDescription: null
        }
    }

    static createNewPlanRequestMeta(): IPlanRequestMetaWrapper {
        return {
            userPlanId: null,
            userPlanObj: {
                userId: null,
                secondaryUserId: null,
                isSelf: null,
                planSource: null,
                statuses: null,
                planMeta: {
                    sourceFamilyMap: {}
                }
            }
        }
    }

    static createNewAddUserMetricObj(userId: string, patientId: number, isSelf: boolean, metricId: string, value: number): IAddMetric {
        return {
            userId: userId,
            subUserId: patientId.toString(),
            self: isSelf,
            measureMeta: null,
            metricId: Number(metricId),
            value: value,
            source: "DOCSAPP",
            validated: true,
            metricDate: new Date().getTime()
        }
    }

    static createUserMetricReqObject(userId: string, subUserId: string, metricId: number, isSelf: boolean): IMetricOptions {
        return {
            userId,
            subUserId,
            metricId,
            isSelf
        }
    }

    static getAlbusReferralFromConsultationPlan(plan: ConsultationPrescriptionItemRequest): IDoctorCheckInInfo {
        let doctorCheckIn = ModelFactory.createNewDoctorCheckinInfo();
        if (!_.isEmpty(plan.activities)) {
            doctorCheckIn.doctorType = { code: plan.activities[0].doctorType };
            if (!_.isEmpty(plan.activities[0].schedule) && !_.isEmpty(plan.activities[0].schedule.repetition.unit)
                && AppUtils.isDefined(plan.activities[0].schedule.frequency) && plan.activities[0].schedule.frequency.toString() !== "") {
                doctorCheckIn.activityDate = {
                    count: plan.activities[0].schedule.repetition.count,
                    unit: plan.activities[0].schedule.repetition.unit
                }
            }
            doctorCheckIn.comment = !_.isEmpty(plan.activities[0].comments) ? plan.activities[0].comments[0] : "";
            doctorCheckIn.id = plan._id;
            doctorCheckIn.editInfo = plan.editInfo;
            doctorCheckIn.status = plan.status;
            doctorCheckIn.startEpoch = plan.startEpoch;
            doctorCheckIn.endEpoch = plan.endEpoch;
            doctorCheckIn.newPlanSource = plan.newPlanSource;
        }
        return doctorCheckIn;
    }

    static createNewAuthorObj(name?: string, label?: string, signature?: string, kmc?: string, serviceId?: string) {
        return {
            name: !_.isEmpty(name) ? name : null,
            label: !_.isEmpty(label) ? label : null,
            signature: !_.isEmpty(signature) ? signature : null,
            kmc: !_.isEmpty(kmc) ? kmc : null,
            serviceId: serviceId || null
        }
    }

    static createNewFitnessPlanGuideline (metaObj: any): PrescriptionItemFitnessGuideline {
        return {
            guideline: null,
            meta: metaObj || null
        }
    }

    static createNewPatientUIVitals (avgValue: string, unit: string, vitalName: string, value: string, recordDate: number): IPatientVitalsUI {
        return {
            avgValue: avgValue || null,
            unit: unit || null,
            vitalName: vitalName || null,
            vital: {
                value: value || null,
                recordDate: recordDate || null
            }
        }
    }

    static createNewGoalItem(): any {
        return {
            goalName: null,
            current: null,
            metric: null,
            aim: null,
            target: null
        }
    }

    static createTemplatedListDefaultItem(widgetConfig: any): any {
        let defaultItem: {[key: string]: any} = {}
        widgetConfig && !_.isEmpty(widgetConfig.answerList) && !_.isEmpty(widgetConfig.answerList[0].fields) && widgetConfig.answerList[0].fields.forEach((fieldConfig: any) => {
            if (fieldConfig.key !== "header" && fieldConfig.key !== "emptyRow") {
                if (fieldConfig.configuration && AppUtils.isDefined(fieldConfig.configuration.default)) {
                    defaultItem[fieldConfig.key] = fieldConfig.configuration.default;
                } else {
                    defaultItem[fieldConfig.key] = null;
                }
            }
        })
        return defaultItem;
    }

    static createTreatmentPlanSaveObject(diagnosis: string[] = [], template: any = null, templateName: string = null): ITreatmentPlanTemplateSaveObj {
        return {
            diagnosis,
            doctorId: null,
            template,
            templateName
        }
    }
}